import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/**
 * Objects that can carry notes. The value is the resource path — the note ids
 * live on the parent as its `notes` field, so attaching or detaching a note is
 * a partial POST to the parent, not a call on the note itself.
 */
export const NOTE_PARENTS = {
  lineItem: 'lineitems',
  lineItemTemplate: 'lineitemtemplates',
  order: 'orders',
  site: 'sites',
  adUnit: 'adunits',
  earningsAccount: 'earningsaccounts',
} as const

export type NoteParentType = keyof typeof NOTE_PARENTS

export interface NoteUserRef {
  id: string
  name: string
}

export interface Note {
  id: string
  /**
   * The note body, one entry per line. The API stores notes as a list of
   * strings; `dataType` says whether each entry is plain text or HTML.
   */
  data: string[]
  dataType: 'TEXT' | 'HTML'
  objectState: string
  createTime: string
  updateTime: string
  createUser: NoteUserRef | null
  updateUser: NoteUserRef | null
}

export interface NoteInput {
  /** Omitted fields are left as they are — the API treats a POST as a patch. */
  data?: string[]
  dataType?: 'TEXT' | 'HTML'
  objectState?: string
}

function mapUserRef(v: unknown): NoteUserRef | null {
  if (!v || typeof v !== 'object') return null
  const r = v as Record<string, unknown>
  return { id: (r.id as string) ?? '', name: (r.name as string) ?? '' }
}

function mapNote(r: Record<string, unknown>): Note {
  return {
    id: (r.id as string) ?? '',
    data: (r.data as string[]) ?? [],
    dataType: (r.dataType as Note['dataType']) ?? 'TEXT',
    objectState: (r.objectState as string) ?? '',
    createTime: (r.createTime as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
    createUser: mapUserRef(r.createUser),
    updateUser: mapUserRef(r.updateUser),
  }
}

/**
 * The `id` query parameter takes a semicolon-separated vector, so a parent's
 * whole note list is one request. Kept under the 100-row page cap: a parent
 * with more notes than that shows the most recent ones.
 */
const PAGE_SIZE = 100

export const NotesApi = {
  /**
   * The notes with these ids, newest first. Returns nothing for an empty list
   * rather than falling through to an unfiltered query, which would answer
   * with every note in the network.
   */
  getByIds: async (ids: string[]): Promise<Note[]> => {
    if (ids.length === 0) return []
    const params = new URLSearchParams({
      context: getContext(),
      id: ids.join(';'),
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'false',
      orderBy: 'createTime',
      orderByDirection: 'DESCENDING',
      page: '1',
      pageSize: String(Math.min(ids.length, PAGE_SIZE)),
    })
    const res = await apiFetch(`${API_BASE}/notes?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch notes')
    return toArray(await res.json()).map(mapNote)
  },

  getById: async (id: string): Promise<Note> => {
    const res = await apiFetch(`${API_BASE}/notes/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch note')
    return mapNote(await res.json())
  },

  /**
   * Writes a note. The endpoint creates on an unknown id and updates on a
   * known one, so a new note is a `save` with a freshly generated id — see
   * {@link newNoteId}.
   */
  save: async (id: string, input: NoteInput): Promise<Note> => {
    const res = await apiFetch(`${API_BASE}/notes/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save note')
    return mapNote(await res.json())
  },

  /**
   * Hides a note. The API has no DELETE, so removing a note means hiding it —
   * detach it from its parent as well to keep it out of the parent's list.
   */
  hide: async (id: string): Promise<Note> => NotesApi.save(id, { objectState: 'HIDDEN' }),

  /**
   * Replaces a parent's note list. The parent's `notes` field is posted whole,
   * so every note it should keep must be in `noteIds`.
   */
  setParentNoteIds: async (parent: NoteParentType, parentId: string, noteIds: string[]): Promise<string[]> => {
    const res = await apiFetch(`${API_BASE}/${NOTE_PARENTS[parent]}/${parentId}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ notes: noteIds.map((id) => ({ id })) }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save notes')
    const saved = (await res.json()) as Record<string, unknown>
    return ((saved.notes as Record<string, unknown>[]) ?? []).map((n) => (n.id as string) ?? '')
  },
}

/** Ids are client-generated, matching how the rest of the app creates objects. */
export function newNoteId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16)
}
